import { memo, type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ViewTypeName } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { ConfigViewVisbilityOptions } from '~/components/ViewControls/ViewVisiblityToggleMenu/ConfigViewVisbilityOptions';
import { ConfigViewVisibilityOptionsSortable } from '~/components/ViewControls/ViewVisiblityToggleMenu/ConfigViewVisibilityOptionsSortable';
import { CustomFieldsVisbilityOptions } from '~/components/ViewControls/ViewVisiblityToggleMenu/CustomFieldsVisbilityOptions';
import { ViewVisibilityMenuSection } from '~/components/ViewControls/ViewVisiblityToggleMenu/shared/ViewVisibilityMenuSection';
import { ViewCardSizePreference } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewCardSizePreference';
import { ViewVisibilityToggleMenu } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewVisibilityToggleMenu';
import { usePublicCustomFieldsContext } from '~/providers/PublicCustomFieldsProvider';
import {
  configViewVisiblityOptionsSelector,
  currentViewTypeNameSelector,
  customFieldVisibilityOptionsSelector,
} from '~/store/configViews/selectors';

export type PublicViewVisibilityToggleMenuProps = {
  extraItems?: ReactNode;
};

export const PublicViewVisibilityToggleMenu = memo(({ extraItems }: PublicViewVisibilityToggleMenuProps) => {
  const customFieldOptions = useSelector(customFieldVisibilityOptionsSelector);
  const configViewOptions = useSelector(configViewVisiblityOptionsSelector);
  const showCustomFieldsOptions = customFieldOptions.length > 0;
  const showConfigViewOptions = configViewOptions.length > 0;
  const { customFields } = usePublicCustomFieldsContext();
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const { presentationSettings } = useShortIdContext();
  const areViewSettingsLocked = useMemo(
    () => presentationSettings?.lockedControls?.metadataFieldsVisible === true,
    [presentationSettings?.lockedControls?.metadataFieldsVisible],
  );
  const hasExtraItems = extraItems != null;
  const extraItemsGroup = hasExtraItems ? (
    <fieldset className="m-0 border-0 p-0">
      <legend className="sr-only">Expand nested content</legend>
      {extraItems}
    </fieldset>
  ) : null;

  if (areViewSettingsLocked) {
    if (!hasExtraItems) {
      return null;
    }

    return <ViewVisibilityToggleMenu>{extraItemsGroup}</ViewVisibilityToggleMenu>;
  }

  if (currentViewTypeName === ViewTypeName.table) {
    return (
      <ViewVisibilityToggleMenu>
        <div className="flex flex-col gap-2">
          {extraItemsGroup}
          {showConfigViewOptions && (
            <>
              {hasExtraItems && <hr className="-mx-2 h-px border-0 ads:bg-ads-gray-4 bg-gray-4" />}
              <ViewVisibilityMenuSection label="Visibility" spacing="compact">
                <ConfigViewVisibilityOptionsSortable
                  metadataOptions={configViewOptions}
                  customFieldOptions={customFieldOptions}
                />
              </ViewVisibilityMenuSection>
            </>
          )}
        </div>
      </ViewVisibilityToggleMenu>
    );
  }

  const showCustomFieldsSection = showCustomFieldsOptions && !!customFields;
  const showVisibilitySection = showConfigViewOptions || showCustomFieldsSection || hasExtraItems;

  return (
    <ViewVisibilityToggleMenu>
      <div className="flex flex-col gap-2">
        {currentViewTypeName === ViewTypeName.gallery && (
          <ViewVisibilityMenuSection label="Display">
            <ViewCardSizePreference />
          </ViewVisibilityMenuSection>
        )}
        {currentViewTypeName === ViewTypeName.gallery && showVisibilitySection && (
          <hr className="-mx-2 h-px border-0 ads:bg-ads-gray-4 bg-gray-4" />
        )}
        {showVisibilitySection && (
          <ViewVisibilityMenuSection label="Visibility" spacing="compact">
            {showConfigViewOptions && <ConfigViewVisbilityOptions options={configViewOptions} />}
            {showCustomFieldsSection && (
              <CustomFieldsVisbilityOptions customFields={customFields} options={customFieldOptions} />
            )}
            {extraItemsGroup}
          </ViewVisibilityMenuSection>
        )}
      </div>
    </ViewVisibilityToggleMenu>
  );
});

PublicViewVisibilityToggleMenu.displayName = 'PublicViewVisibilityToggleMenu';
